import type { ReactNode } from 'react'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import type { SxProps } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'

export interface InsightTransactionRowProps {
  /** Leading visual: merchant/institution logo, category icon, or date badge. */
  leading?: ReactNode
  /** Primary line (e.g. merchant, payee, transaction name). */
  title: ReactNode
  /** Secondary line beneath the title (e.g. category). */
  subtitle: ReactNode
  /** Trailing amount, pre-formatted by the caller. */
  amount: ReactNode
  /** Theme token for the amount text (e.g. 'success.main' for credits). */
  amountColor?: string
  sx?: SxProps
}

/**
 * A single transaction/list row for the insights-v2 transaction-list family:
 * leading visual + title/subtitle on the left, amount on the right. Named
 * `InsightTransactionRow` to avoid colliding with the transactions widget's
 * public `TransactionRow` export.
 */
const InsightTransactionRow = ({
  amount,
  amountColor = 'text.primary',
  leading,
  subtitle,
  title,
  sx,
}: InsightTransactionRowProps) => (
  <Stack
    sx={{
      // The amount shares the title's top line while the subtitle sits below, so
      // the row is top-aligned and the inner left group is centered.
      alignItems: 'flex-start',
      flexDirection: 'row',
      gap: 16,
      px: 16,
      py: 14,
      width: '100%',
      ...sx,
    }}
  >
    <Stack sx={{ alignItems: 'center', flexDirection: 'row', flexGrow: 1, gap: 12, minWidth: 0 }}>
      {leading && <Box sx={{ flexShrink: 0 }}>{leading}</Box>}
      <Stack sx={{ minWidth: 0 }}>
        <Text bold={true} truncate={true} variant="body1">
          {title}
        </Text>
        <Text color="text.secondary" truncate={true} variant="caption">
          {subtitle}
        </Text>
      </Stack>
    </Stack>
    <Text bold={true} color={amountColor} sx={{ flexShrink: 0 }} variant="body1">
      {amount}
    </Text>
  </Stack>
)

export default InsightTransactionRow
